import React, { useState, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  Filter,
  ZoomIn,
  ZoomOut,
  AlertTriangle,
  Bookmark,
  RefreshCw,
  Settings,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ViewMode, TaskStatus, TaskPriority } from '../../types/schedule';

interface ScheduleToolbarProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onToday?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onExport?: () => void;
  onImport?: () => void;
  onCreateBaseline?: () => void;
  onShowCriticalPath?: (show: boolean) => void;
  showCriticalPath?: boolean;
  onRefresh?: () => void;
  onOpenSettings?: () => void;
  // Filter props
  statusFilter?: TaskStatus[];
  onStatusFilterChange?: (statuses: TaskStatus[]) => void;
  priorityFilter?: TaskPriority[];
  onPriorityFilterChange?: (priorities: TaskPriority[]) => void;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  isLoading?: boolean;
}

const viewModeOptions: { value: ViewMode; label: string; shortLabel: string }[] = [
  { value: 'day', label: 'Day View', shortLabel: 'Day' },
  { value: 'week', label: 'Week View', shortLabel: 'Week' },
  { value: 'month', label: 'Month View', shortLabel: 'Month' },
  { value: 'quarter', label: 'Quarter View', shortLabel: 'Qtr' },
];

const statusOptions: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'not_started', label: 'Not Started', color: 'bg-gray-100 text-gray-800' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const priorityOptions: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'bg-green-100 text-green-800' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-800' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-800' },
];

const ScheduleToolbar: React.FC<ScheduleToolbarProps> = ({
  viewMode,
  onViewModeChange,
  onZoomIn,
  onZoomOut,
  onToday,
  onPrevious,
  onNext,
  onExport,
  onImport,
  onCreateBaseline,
  onShowCriticalPath,
  showCriticalPath = true,
  onRefresh,
  onOpenSettings,
  statusFilter = [],
  onStatusFilterChange,
  priorityFilter = [],
  onPriorityFilterChange,
  onClearFilters,
  hasActiveFilters = false,
  isLoading = false,
}) => {
  const { t: _t } = useTranslation();
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const handleStatusToggle = (status: TaskStatus) => {
    if (!onStatusFilterChange) return;

    if (statusFilter.includes(status)) {
      onStatusFilterChange(statusFilter.filter(s => s !== status));
    } else {
      onStatusFilterChange([...statusFilter, status]);
    }
  };

  const handlePriorityToggle = (priority: TaskPriority) => {
    if (!onPriorityFilterChange) return;

    if (priorityFilter.includes(priority)) {
      onPriorityFilterChange(priorityFilter.filter(p => p !== priority));
    } else {
      onPriorityFilterChange([...priorityFilter, priority]);
    }
  };

  return (
    <div className="flex items-center justify-between px-4 py-1.5 bg-white border-b border-gray-100">
      {/* Left Section - Navigation & View Mode */}
      <div className="flex items-center space-x-1.5">
        {/* Date Navigation */}
        <div className="flex items-center border border-gray-200 rounded-md">
          <button
            onClick={onPrevious}
            className="p-1 hover:bg-gray-50 transition-colors rounded-l-md border-r border-gray-200"
            title="Previous"
          >
            <ChevronLeft className="h-3.5 w-3.5 text-gray-500" />
          </button>
          <button
            onClick={onToday}
            className="px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Today
          </button>
          <button
            onClick={onNext}
            className="p-1 hover:bg-gray-50 transition-colors rounded-r-md border-l border-gray-200"
            title="Next"
          >
            <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
          </button>
        </div>

        {/* Divider */}
        <div className="h-4 w-px bg-gray-200" />

        {/* View Mode Toggle */}
        <div className="flex items-center bg-gray-100 rounded-md p-0.5">
          {viewModeOptions.map(option => (
            <button
              key={option.value}
              onClick={() => onViewModeChange(option.value)}
              className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${
                viewMode === option.value
                  ? 'bg-white text-brand-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title={option.label}
            >
              {option.shortLabel}
            </button>
          ))}
        </div>

        {/* Zoom Controls */}
        {(onZoomIn || onZoomOut) && (
          <>
            <div className="h-4 w-px bg-gray-200" />
            <div className="flex items-center">
              <button
                onClick={onZoomOut}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="h-3 w-3" />
              </button>
              <button
                onClick={onZoomIn}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="h-3 w-3" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Center Section - Critical Path Toggle */}
      <div className="flex items-center">
        {onShowCriticalPath && (
          <button
            onClick={() => onShowCriticalPath(!showCriticalPath)}
            className={`inline-flex items-center px-2 py-1 text-[10px] font-medium rounded transition-colors ${
              showCriticalPath ? 'bg-red-50 text-red-600' : 'text-gray-500 hover:bg-gray-100'
            }`}
            title="Toggle Critical Path"
          >
            <AlertTriangle className="h-3 w-3 mr-1" />
            Critical
          </button>
        )}
      </div>

      {/* Right Section - Filters & Actions */}
      <div className="flex items-center space-x-1">
        {/* Filter Dropdown */}
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className={`inline-flex items-center px-2 py-1 text-[10px] font-medium rounded transition-colors ${
              hasActiveFilters ? 'bg-red-50 text-red-600' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            <Filter className="h-3 w-3 mr-1" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 text-[9px] font-bold rounded-full bg-red-500 text-white">
                {statusFilter.length + priorityFilter.length}
              </span>
            )}
          </button>

          {showFilterDropdown && (
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-gray-900">Filters</h4>
                  {hasActiveFilters && (
                    <button
                      onClick={() => {
                        onClearFilters?.();
                        setShowFilterDropdown(false);
                      }}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* Status Filter */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Status
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => handleStatusToggle(option.value)}
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                          statusFilter.includes(option.value)
                            ? option.color + ' ring-2 ring-offset-1 ring-gray-400'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Priority
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {priorityOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => handlePriorityToggle(option.value)}
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                          priorityFilter.includes(option.value)
                            ? option.color + ' ring-2 ring-offset-1 ring-gray-400'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-3">
                <button
                  onClick={() => setShowFilterDropdown(false)}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-4 w-px bg-gray-200" />

        {/* Action Buttons - Compact */}
        {onCreateBaseline && (
          <button
            onClick={onCreateBaseline}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Create Baseline"
          >
            <Bookmark className="h-3.5 w-3.5" />
          </button>
        )}

        {onImport && (
          <button
            onClick={onImport}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Import Schedule"
          >
            <Upload className="h-3.5 w-3.5" />
          </button>
        )}

        {onExport && (
          <button
            onClick={onExport}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Export Schedule"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        )}

        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        )}

        {onOpenSettings && (
          <button
            onClick={onOpenSettings}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            title="Schedule Settings"
          >
            <Settings className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ScheduleToolbar;
